  import React, { useState, useEffect, useCallback } from "react";
  import classes from "./style.module.scss";
  import { useAppHeader } from "~/app/modules/appHeader";
  import usePageTitle from "~/app/hooks/usePageTitle";
  import { useHistory, useLocation } from "react-router-dom";
  import { route } from "~/app/helpers/route";
  import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
  import { useStoreActions } from "~/store";
import useTranslation from "~/app/hooks/useTranslation";

  interface Task {
    id: number;
    project_number: string;
    project_id: number;
    project_name?: string;
    activity_name?: string;
    activity_id?: number;
    machine_intern?: string;
    machine_id?: number;
    machine_name?: string;
    total_time: string;
    total_minutes: number;
    mode_type: number;
    notes?: string;
    date?: string;
  }

  interface LocationState {
    updatedTask?: Task;
    refresh?: boolean;
    discardChanges?: boolean;
    fromActiveTime?: boolean;
    timeManagementId?: number;
  }

      const STORAGE_KEY = 'dailySummary_tasks';
      const ORIGINAL_STORAGE_KEY = 'dailySummary_originalTasks';

      const DailySummary: React.FC = () => {
      const history = useHistory();
      const location = useLocation<LocationState>();
      const { setData, clearData } = useAppHeader();
      const { t } = useTranslation('timeManagement');

      const getDailySummary = useStoreActions((actions: any) => actions.timeManagement.getDailySummary);
      const saveTime = useStoreActions((actions: any) => actions.timeManagement.save);

      const [tasks, setTasks] = useState<Task[]>([]);
      const [totalTime, setTotalTime] = useState<string>('00:00');
      const [currentDate, setCurrentDate] = useState<string>('');
      const [isLoading, setIsLoading] = useState(true);
      const [isSaving, setIsSaving] = useState(false);
      const fromActiveTime = location.state?.fromActiveTime || false;
      const timeManagementId = location.state?.timeManagementId;

      usePageTitle(t('registerHours'));

      useEffect(() => {
      const backLink = fromActiveTime 
        ? route(TIME_MANAGEMENT_ROUTES.activeTime)
        : route(TIME_MANAGEMENT_ROUTES.list);

      setData({
        title: t('registerHours'),
        subTitle: '',
        backLink: backLink,
        isTimeManagementPage: false,
        showUser: false,
        machineId: '',
        projectId: '',
        parentFolderId: 0,
        showMachineMessages: false,
        isIssueDetailPage: false,
        notification: false,
        isAddTimeForm: false,
      });

      return () => clearData();
      }, [setData, clearData, fromActiveTime, t]);

      const recalculateTotalTime = (taskList: Task[]) => {
      const totalMinutes = taskList.reduce((sum, task) => sum + task.total_minutes, 0);
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      };

      const saveTasksToStorage = (tasksToSave: Task[], originalTasksToSave: Task[]) => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tasksToSave));
        sessionStorage.setItem(ORIGINAL_STORAGE_KEY, JSON.stringify(originalTasksToSave));
      } catch (error) {
        console.error('Error saving to sessionStorage:', error);
      }
      };

      const loadTasksFromStorage = (): { tasks: Task[], originalTasks: Task[] } | null => {
      try {
        const storedTasks = sessionStorage.getItem(STORAGE_KEY);
        const storedOriginalTasks = sessionStorage.getItem(ORIGINAL_STORAGE_KEY);
        
        if (storedTasks && storedOriginalTasks) {
          return {
            tasks: JSON.parse(storedTasks),
            originalTasks: JSON.parse(storedOriginalTasks)
          };
        }
      } catch (error) {
        console.error('Error loading from sessionStorage:', error);
      }
      return null;
      };

      const clearStorage = () => {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(ORIGINAL_STORAGE_KEY);
      };

      const fetchDailySummary = useCallback(async () => {
      try {
        setIsLoading(true);
        
        const today = new Date();
        const dateString = today.toISOString().split('T')[0];
        
        const months = [
          t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
          t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
        ];
        const day = today.getDate();
        const month = months[today.getMonth()];
        const year = today.getFullYear();
        const formattedDate = `${day}. ${month} ${year}`;
        
        setCurrentDate(formattedDate);
        const response = await getDailySummary({ date: dateString });
        
        if (response?.data) {
          const tasksData = response.data.tasks || [];
          const originalData = JSON.parse(JSON.stringify(tasksData));
          setTasks(tasksData);
          setTotalTime(recalculateTotalTime(tasksData));
          
          saveTasksToStorage(tasksData, originalData);
        }
      } catch (error) {
        console.error('Error fetching daily summary:', error);
      } finally {
        setIsLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getDailySummary]);
    
      useEffect(() => {
      const handleDataLoad = async () => {
        if (location.state?.updatedTask) {
          const updatedTask = location.state.updatedTask;
          sessionStorage.removeItem('navigatingToEdit');
          const storedData = loadTasksFromStorage();
          
          if (storedData) {
            const updatedTasks = storedData.tasks.map(task => 
              task.id === updatedTask.id ? updatedTask : task
            );
            
            setTasks(updatedTasks);
            setTotalTime(recalculateTotalTime(updatedTasks));
            
            saveTasksToStorage(updatedTasks, storedData.originalTasks);
            setIsLoading(false);
          } else {
            await fetchDailySummary();
            setTasks(prevTasks => {
              const newTasks = prevTasks.map(task => 
                task.id === updatedTask.id ? updatedTask : task
              );
              setTotalTime(recalculateTotalTime(newTasks));

              return newTasks;
            });
          }
          
          window.history.replaceState({}, document.title);
          return;
        }
        
        if (location.state?.discardChanges) {
          console.log('Discarding all changes');
          const storedData = loadTasksFromStorage();
          if (storedData) {
            setTasks([...storedData.originalTasks]);
            setTotalTime(recalculateTotalTime(storedData.originalTasks));
            saveTasksToStorage(storedData.originalTasks, storedData.originalTasks);
          }
          setIsLoading(false);
          window.history.replaceState({}, document.title);
          return;
        }
        clearStorage();
        sessionStorage.removeItem('navigatingToEdit');
        await fetchDailySummary();
      };

      handleDataLoad();
      }, [location.state, fetchDailySummary]); 

 
      const handleEditTask = (task: Task) => {
      sessionStorage.setItem('navigatingToEdit', 'true');
        history.push({
        pathname: `/timeManagement/${task.id}`,
        state: { 
          taskData: task, 
          returnTo: 'dailySummary',
          fromActiveTime: fromActiveTime,
          timeManagementId: timeManagementId
        }
      });
      };

      const handleSaveTimers = async () => {
      try {
        setIsSaving(true);
        const tasksToSave = tasks.map(task => ({
          id: task.id,
          date: task.date,
          project_id: parseInt(String(task.project_id)),
          machine_id: task.machine_id || null,
          activity_id: task.activity_id || null,
          time: task.total_time,
          mode_type: task.mode_type,
          notes: task.notes || null,
          status: 1,  
          total_time: task.total_minutes
        }));
        const response = await saveTime({ tasks: tasksToSave });
        if (response?.data?.result === 'success' || response?.result === 'success') {
          clearStorage();
          
          history.push({
            pathname: route(TIME_MANAGEMENT_ROUTES.list),
            state: {
              message: t('timersSavedSuccessfully')
            }
          });
        } else {
          throw new Error('Save failed');
        }
        
      } catch (error: any) {        
        const errorMsg = error?.message || String(error);
        if (errorMsg.includes('tasks saved successfully')) {
          clearStorage();
          history.push({
            pathname: route(TIME_MANAGEMENT_ROUTES.list),
            state: {
              message: t('timersSavedSuccessfully')
            }
          });
        } else {
          console.log(t('failedToSaveTimers'));
        }
      } finally {
        setIsSaving(false);
      }
      };
        

        
      const handleGoBack = () => {
      clearStorage();
      sessionStorage.removeItem('navigatingToEdit');

      if (fromActiveTime && timeManagementId) {
        history.push({
          pathname: route(TIME_MANAGEMENT_ROUTES.activeTime),
          state: {
            timeManagementId: timeManagementId,
            isPaused: true,
            returnFromSummary: true
          }
        });
      } else {
        history.push(route(TIME_MANAGEMENT_ROUTES.list));
      }
      };

      if (isLoading) {
      return (
        <div className={classes.dailySummaryWrapper}>
          <div className={classes.content}>
            <div style={{ textAlign: 'center', padding: '40px' }}>{t('loading')}</div>
          </div>
        </div>
      );
      }

      return (
      <div className={classes.dailySummaryWrapper}>
        <div className={classes.content}>
          <div className={classes.dateSection}>
            <div className={classes.dateText}>{currentDate}</div>
          </div>

          <div className={classes.totalSection}>
            <div className={classes.totalLabel}>{t('total')}</div>
            <div className={classes.totalTime}>{totalTime}</div>
          </div>

          <div className={classes.tasksSection}>
            <div className={classes.sectionHeader}>{t('tasks').toUpperCase()}</div>
            
            {tasks.length === 0 ? (
              <div className={classes.noTasks}>
                <p>{t('noTasksRegisteredToday')}</p>
              </div>
            ) : (
              <div className={classes.tasksList}>
                {tasks.map((task) => (
                  <div key={task.id} className={classes.taskCard}>
                    <div className={classes.taskInfo}>
                      <div className={classes.taskNumber}>{task.project_number}</div>
                      {task.mode_type === 0 && task.machine_intern && (
                        <div className={classes.taskMeta}>
                          {t('machine')}: {task.machine_intern}
                        </div>
                      )}
                      {(task.mode_type === 0 || task.mode_type === 1) && task.activity_name && (
                        <div className={classes.taskMeta}>
                          {t('activity')}: {task.activity_name}
                        </div>
                      )}
                     {task.mode_type === 2 && (
                        <div className={classes.taskMeta}>{t('unspecified')}</div>
                      )}
                      {task.mode_type === 2 && task.notes && (
                        <div className={classes.taskMeta}>
                          {t('notes') || 'Notes'}: {task.notes}
                        </div>
                      )}
                    </div>
                    <div className={classes.taskActions}>
                      <div className={classes.taskTime}>{task.total_time}</div>
                      <button 
                        className={classes.editButton}
                        onClick={() => handleEditTask(task)}
                      >
                        ✎
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={classes.buttonSection}>
            <button 
              className={classes.saveButton}
              onClick={handleSaveTimers}
              disabled={isSaving || tasks.length === 0}
            >
              {isSaving ? t('saving') : t('saveHours')}
            </button>
            
            <button 
              className={classes.backButton}
              onClick={handleGoBack}
              disabled={isSaving}
            >
              {t('back')}
            </button>
          </div>
        </div>
      </div>
      );
      };

      export default DailySummary;